// Unified accessors for initial nodes across legacy/new shapes

// activityInfoLike: axios-like { data: {...} } or raw object { activityId, settings, initialNodes? }
export function getInitialConfig(activityInfoLike) {
  const data = activityInfoLike?.data ?? activityInfoLike ?? {};
  const settings = data.settings ?? {};

  let nodes = [];
  
  // 1. 優先處理新的分組結構 (initialNodeGroups)
  const groups = settings.initialNodeGroups;
  if (Array.isArray(groups) && groups.length > 0) {
    // 找到需要自動顯示的群組：
    // - timing 為 'immediate' 的群組（活動開始時自動顯示）
    // - isDefault 為 true 的群組（預設群組，相當於舊版本的 initialNodes）
    const immediateGroups = groups.filter(group => 
      group.timing === 'immediate' || group.isDefault === true
    );
    
    // 從這些群組中提取所有節點
    immediateGroups.forEach(group => {
      if (Array.isArray(group.nodes)) {
        nodes.push(...group.nodes);
      }
    });
  }
  
  // 2. 如果沒有找到分組數據，則回退到舊格式
  if (nodes.length === 0) {
    if (data.initialNodes && typeof data.initialNodes === 'object') {
      nodes = Array.isArray(data.initialNodes.nodes)
        ? data.initialNodes.nodes
        : Array.isArray(data.initialNodes)
          ? data.initialNodes
          : [];
    } else if (settings.initialNodes && typeof settings.initialNodes === 'object') {
      nodes = Array.isArray(settings.initialNodes.nodes)
        ? settings.initialNodes.nodes
        : Array.isArray(settings.initialNodes)
          ? settings.initialNodes
          : [];
    }
  }

  // Filter out any intro nodes (legacy support, but don't create new ones)
  nodes = nodes.filter(n => n && n.type !== 'intro');

  return { nodes };
}

export function normalizeNodeTag(tag, nodeId) {
  const firstStringTag = Array.isArray(tag)
    ? tag.find(value => typeof value === 'string' && value.trim())
    : tag;
  const fallbackTag = typeof nodeId === 'number' && nodeId < 0 ? 'initial' : 'idea';
  const normalizedTag = typeof firstStringTag === 'string' && firstStringTag.trim()
    ? firstStringTag.trim()
    : fallbackTag;

  return normalizedTag === 'initial' && typeof nodeId === 'number' && nodeId > 0
    ? 'idea'
    : normalizedTag;
}

// Helper function to convert seed nodes to display nodes format
export function convertSeedNodesToDisplayNodes(seedNodes, options = {}) {
  const {
    prefixActivityDescription = false,
    activityDescription = '',
    groupId = null, // 必須從頁面層級傳入
  } = options;

  if (!Array.isArray(seedNodes) || seedNodes.length === 0) {
    return [];
  }

  return seedNodes.map((node, index) => {
    let nodeContent = node.content || '';
    
    // Optionally prefix with activity description
    if (prefixActivityDescription && activityDescription) {
      nodeContent = activityDescription + (nodeContent ? '\n\n---\n\n' + nodeContent : '');
    }

    const baseNode = {
      id: (typeof node.id === 'number' && Number.isInteger(node.id) && node.id < 0)
        ? node.id   // Preserve valid negative integer IDs from settings
        : -(index + 1), // Fall back for legacy "node-xxx" strings or missing IDs
      title: node.title || '',
      content: nodeContent,
      author: '主持人',
      tags: normalizeNodeTag(node.tags, (typeof node.id === 'number' && Number.isInteger(node.id) && node.id < 0)
        ? node.id
        : -(index + 1)),
      isInitial: true, // 標記為初始節點，避免嘗試從後端 API 獲取
      createdAt: new Date().toISOString(),
      groupId: groupId, // 使用傳入的 groupId，不再從 storage 讀取
    };

    return baseNode;
  });
}

// Normalize outgoing settings for PUT to ensure a single source-of-truth at settings.initialNodes
export function normalizeSettingsForInitialNodes(settings) {
  const safe = settings ? { ...settings } : {};
  const nodesArrayCandidate = safe?.initialNodes?.nodes ?? safe?.initialNodes;
  let nodes = Array.isArray(nodesArrayCandidate) ? nodesArrayCandidate
    : Array.isArray(nodesArrayCandidate?.nodes) ? nodesArrayCandidate.nodes
    : [];

  // Filter out any intro nodes (no longer supported)
  nodes = nodes.filter(n => n && n.type !== 'intro');

  // Canonical: only nodes under initialNodes
  safe.initialNodes = { nodes };
  // Remove legacy intro field to fully migrate
  if ('intro' in safe) delete safe.intro;

  return safe;
}

